import imageCompression from "browser-image-compression";
import axios, { AxiosInstance } from "axios";
import {
  IUserInfo,
  IEntry,
  IEntryDocuments,
  IDocumentIds,
} from "../types/appDataTypes";
import { IAppConfig } from "../reducers/configReducers";
import { dispatch, getState } from "../store";
import { appActions } from "../actions";
import { errorHandler } from "./apiErrorHandler";

export interface IAddressResponse {
  addressFormat?: string;
  streetAddress?: string;
  postalCode?: string;
  blockNumber?: string;
  buildingName?: string;
  poBox?: string;
}
export interface ErrorResponse {
  errorCode: string;
  errorMessage: string;
}

export interface IApplicationResponse {
  appId: string;
  formId: string;
  country: IUserInfo["activeCountry"];
}

export enum API_PATH {
  ADDRESS = "/address/postalCode",
  APPLICATION = "application",
  DOCUMENT = "document",
  CHECK_VALIDITY = "checkvalidity",
  CHECK_DOB = "checkdob",
  SUBMIT = "submit",
}
function getBase64(file: File) {
  var reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = function () {
    console.log(reader.result);
  };
  reader.onerror = function (error) {
    console.log("Error: ", error);
  };
}

class ApiMiddleware {
  baseURL: string = "";
  ajax: AxiosInstance = axios;
  init = (appConfig: IAppConfig, country: IUserInfo["activeCountry"]) => {
    this.baseURL = appConfig.api.replace("{country}", country.toLowerCase());
    this.ajax = axios.create({
      baseURL: this.baseURL,
      timeout: 60000,
    });

    this.ajax.interceptors.request.use((request) => {
      dispatch(appActions.setIsCallingApi(true));
      return request;
    });

    this.ajax.interceptors.response.use(
      (response) => {
        return this.interceptResponseSucceess(response);
      },
      (error) => {
        dispatch(appActions.setIsCallingApi(false));
        errorHandler(error, this.ajax);
        return error.response ? error.response : error;
      }
    );
  };

  interceptResponseSucceess = (response: any) => {
    dispatch(appActions.setIsCallingApi(false));
    const url = response?.config?.url;
    if (url) {
      const urlPaths = url.split("/");
      const updateEntryUrl =
        urlPaths[0] === "application" &&
        urlPaths[2] === "entry" &&
        urlPaths[4] === "update";
      // reset retry count for other API, update entry need to call upload API before reset
      if (!updateEntryUrl) {
        dispatch(appActions.updateErrorRetryCount(0));
      }
    }
    return response;
  };

  // Get address by postal code
  getAddress = (postalCode: string) => {
    return this.ajax.get<IAddressResponse>(API_PATH.ADDRESS, {
      baseURL: "/uobandart/api/v1",
      params: { postalCode },
    });
  };

  // Initial Application
  postInitApplication = (userInfo: IUserInfo, reCaptchaKey: string) => {
    return this.ajax.post<IApplicationResponse | ErrorResponse>(
      `${API_PATH.APPLICATION}/${reCaptchaKey}`,
      userInfo
    );
  };

  // Delete Application
  deleteApplication = (id: string) => {
    return this.ajax.post(`${API_PATH.APPLICATION}/id-number/${id}/delete`);
  };

  // Update Application
  updateApplication = (userInfo: IUserInfo) => {
    dispatch(appActions.setApplicationUpdated(true));
    return this.ajax.post<IApplicationResponse>(
      `${API_PATH.APPLICATION}/${userInfo.appId}/update`,
      userInfo
    );
  };

  // Update Biography
  updateBiography = (biography: string) => {
    const userInfo = getState().userInfo;
    userInfo.artistInfo.biography = biography;
    return this.ajax.post(
      `${API_PATH.APPLICATION}/${userInfo.appId}/update`,
      userInfo,
      { baseURL: this.baseURL }
    );
  };

  getApplication = (appId: string) => {
    return this.ajax.get<IUserInfo>(
      `${API_PATH.APPLICATION}/${appId}?t=${Date.now()}`
    );
  };

  // Create Entry
  createEntry = (appId: string, entry: IEntry) => {
    dispatch(appActions.setApplicationUpdated(true));
    return this.ajax.post<IEntry>(`application/${appId}/entry`, entry);
  };

  // Update Entry
  updateEntry = (appId: string, entry: IEntry) => {
    dispatch(appActions.setApplicationUpdated(true));
    const { title, titleEn, width, height, medium, description } = entry;
    return this.ajax.post<IEntry>(
      `application/${appId}/entry/${entry.entryId}/update`,
      { title, titleEn, width, height, medium, description }
    );
  };

  // Delete Entry
  deleteEntry = (appId: string, entryId: string) => {
    dispatch(appActions.setApplicationUpdated(true));
    return this.ajax.post(`application/${appId}/entry/${entryId}/delete`);
  };

  // Get Entry Documents
  getEntryDocuments = (appId: string, entryId: string) => {
    return this.ajax.get<IEntryDocuments>(
      `application/${appId}/entry/${entryId}/${
        API_PATH.DOCUMENT
      }?t=${Date.now()}`
    );
  };

  // Upload entry document
  uploadDocument = (data: {
    file: File;
    appId: string;
    entryId: string;
    documentId: IDocumentIds;
    entry: IEntry;
    artistStatus: string;
    artworkSizeLimit: number;
  }) => {
    const {
      file,
      appId,
      entryId,
      documentId,
      entry,
      artistStatus,
      artworkSizeLimit,
    } = data;
    const option = {
      maxSizeMB: artworkSizeLimit / 1024 / 1000,
    };
    return imageCompression(file, option)
      .then((x) => {
        const newFile = new File([x], "image.jpg", { type: "image/jpeg" });
        // alert(newFile.size)
        const formData = new FormData();
        const data = [
          { key: "title", item: entry.title },
          { key: "titleEn", item: entry.titleEn },
          { key: "height", item: entry.height?.toString() },
          { key: "width", item: entry.width?.toString() },
          { key: "medium", item: entry.medium },
          { key: "file", item: newFile },
          { key: "artistStatus", item: artistStatus },
        ];
        data.forEach((info) => formData.append(info.key, info.item));
        dispatch(appActions.setApplicationUpdated(true));
        return this.ajax.post<{ id: string; imageUrl: string }>(
          `application/${appId}/entry/${entryId}/${API_PATH.DOCUMENT}/${documentId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      })
      .catch((x) => {
        dispatch(appActions.setIsCallingApi(false));
        return x;
      });
  };

  // Get single entry image
  getEntryDocument = (
    appId: string,
    entryId: string,
    documentId: "p1" | "s1" | "s2"
  ) => {
    return this.ajax.get<string>(
      `application/${appId}/entry/${entryId}/${API_PATH.DOCUMENT}/${documentId}`
    );
  };

  // Delete single image
  deleteEntryImage = (
    appId: string,
    entryId: string,
    documentId: "s1" | "s2"
  ) => {
    dispatch(appActions.setApplicationUpdated(true));
    return this.ajax.post<string>(
      `application/${appId}/entry/${entryId}/${API_PATH.DOCUMENT}/${documentId}/delete`
    );
  };

  // Check Validity ID
  checkValidity = (id: string, recaptchaToken: string) => {
    const path = `application/${id}/${API_PATH.CHECK_VALIDITY}/${recaptchaToken}`;
    return this.ajax.post<IApplicationResponse | ErrorResponse>(path);
  };

  // check Birthday
  checkBirthday = ({ dob, appId }: { dob: string; appId: string }) => {
    return this.ajax.post(`application/${appId}/${API_PATH.CHECK_DOB}/${dob}`);
  };

  // submit application
  submitApplication = (userInfo: IUserInfo) => {
    return this.ajax.post(
      `application/${userInfo.appId}/${API_PATH.SUBMIT}`,
      userInfo
    );
  };
}

const apiMiddleware = new ApiMiddleware();

export default apiMiddleware;
